import React, {useCallback, useEffect, useRef} from "react";
import {SearchAppBar} from "~/apps/core/ui/SearchAppBar";
import {Box, Container} from "@mui/material";
import {CustomsRequirementList} from "~/apps/packages/customs-requirements/components/CustomsRequirementList";
import {
    CustomsRequirementTypeChipInput,
    CustomsRequirementTypeChipInputProps
} from "~/apps/packages/customs-requirements/components/CustomsRequirementTypeChipInput";
import {useHistoryState} from "~/apps/core/utils";
import {CustomsRequirement} from "~/apps/packages/customs-requirements/queries";
import moment from "moment";
import {tuple} from "~/types";
import {
    DateRangeChipInput,
    DateRangeChipInputProps
} from "~/apps/packages/customs-requirements/components/DateRangeChipInput";
import {FiltersBar} from "~/apps/core/ui/FiltersBar";
import {AbsoluteContainer} from "~/apps/core/ui/AbsoluteContainer";
import {useChangeEffect} from "~/lib/useChangeEffect";


export type IndexPageProps = {

}


type FilterState = {
    type?: CustomsRequirement["screeningType"],
    search?: string
    dateRange?: [Date, Date]
}


const useFilterState = () => {
    const [value, setValue, delValue] = useHistoryState<FilterState>({
        name: "filter",
        handleBack: false,
        serialize: state => JSON.stringify(state),
        deserialize: data => {
            const raw = JSON.parse(data);
            const value: FilterState = {};

            if (typeof raw?.search == "string" && raw.search.length > 0) {
                value.search = raw.search;
            }
            if (typeof raw?.type == "string" && raw.type.length > 0){
                value.type = raw.type;
            }
            if (Array.isArray(raw?.dateRange) && raw.dateRange.length == 2) {
                const dateRange = raw.dateRange.map(x => moment(x)).filter(x => x.isValid());
                if (dateRange.length == 2) {
                    value.dateRange = dateRange.map(x => x.toDate()) as any;
                }
            }
            return Object.keys(value).length ? value : null;
        },
    });

    const setFilter = useCallback(async value => {
        if (value) {
            value = {...value};
            if (!value.search) {
                delete value.search;
            }
            if (!value.type) {
                delete value.type;
            }
            if (!value.dateRange){
                delete value.dateRange;
            }
            if (Object.keys(value).length > 0) {
                setValue(value);
                return;
            }
        }
        await delValue();
    }, []);

    return tuple(value, setFilter);
}


export const IndexPage = (props: IndexPageProps) => {
    const $container = useRef<HTMLDivElement>();
    const [filter, setFilter] = useFilterState();
    const title = filter?.search || "Требования";

    useChangeEffect(() => {
        $container.current?.scrollTo({top: 0});
    }, [filter]);

    const handleSearchQueryChange = (query: string) => {
        setFilter({
            ...filter,
            search: query
        }).then(() => null);
    }

    const handleTypeChange: CustomsRequirementTypeChipInputProps["onChange"] = (value) => {
        setFilter({
            ...filter,
            type: value,
        }).then(() => null);
    }

    const handleDateRangeChange: DateRangeChipInputProps["onChange"] = (value) => {
        setFilter({
            ...filter,
            dateRange: value
        }).then(() => null);
    }

    return <>
        <SearchAppBar
            position="fixed"
            color="default"
            title={title}
            onSearch={handleSearchQueryChange}
            query={filter?.search}
        >
            <FiltersBar>
                <CustomsRequirementTypeChipInput
                    value={filter?.type}
                    onChange={handleTypeChange}
                />
                <DateRangeChipInput
                    value={filter?.dateRange}
                    onChange={handleDateRangeChange}
                />
            </FiltersBar>
        </SearchAppBar>
        <AbsoluteContainer disableGutters top={104} ref={$container}>
            <CustomsRequirementList
                query={filter?.search}
                dateRange={filter?.dateRange}
                type={filter?.type}
            />
            <Box minHeight="16px" />
        </AbsoluteContainer>
    </>;
}